import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { comprehensionService } from '@/services/comprehension-service';
import { ComprehensionProfile } from '@/types/comprehension';
import { getConsistentUserId } from '@/utils/user-id-helpers';

export function useComprehensionAssessment() {
  const { user } = useAuth();
  const [shouldShowAssessment, setShouldShowAssessment] = useState(false);
  const [profile, setProfile] = useState<ComprehensionProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastCheckTime, setLastCheckTime] = useState<string | null>(null);
  const isCheckingRef = useRef(false); // Prevent multiple simultaneous API calls
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // Debounce timer

  // Check if assessment should be shown
  const checkAssessmentEligibility = useCallback(async () => {
    const userId = getConsistentUserId(user, null);
    if (!userId || isCheckingRef.current) {
      setIsLoading(false);
      return;
    }

    try {
      isCheckingRef.current = true;
      setIsLoading(true);
      
      // Check if we should show assessment
      const shouldTake = await comprehensionService.shouldTakeAssessment(userId);
      
      // Get current profile
      const currentProfile = await comprehensionService.getComprehensionProfile(userId);
      
      setProfile(currentProfile);
      setShouldShowAssessment(shouldTake);
      setLastCheckTime(new Date().toISOString());
      
      // Store check time to avoid excessive API calls
      localStorage.setItem(`assessment_check_${userId}`, new Date().toISOString());
    } catch (error) {
      console.error('Error checking assessment eligibility:', error);
      // Set safe defaults on error to prevent app crashes
      setProfile(null);
      setShouldShowAssessment(false);
    } finally {
      setIsLoading(false);
      isCheckingRef.current = false;
    }
  }, [user]);

  // Initialize on user change
  useEffect(() => {
    const userId = getConsistentUserId(user, null);
    if (!userId) {
      setIsLoading(false);
      setShouldShowAssessment(false);
      setProfile(null);
      return;
    }

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Check if we recently checked (within last hour)
    const lastCheck = localStorage.getItem(`assessment_check_${userId}`);
    if (lastCheck) {
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const lastCheckDate = new Date(lastCheck);
      
      if (lastCheckDate > hourAgo) {
        // Recently checked, skip API call for now
        setIsLoading(false);
        return;
      }
    }
    
    // Debounce the API call to prevent rapid successive calls
    debounceTimerRef.current = setTimeout(() => {
      checkAssessmentEligibility();
    }, 500); // 500ms debounce

    // Cleanup function to reset checking flag and clear timers if component unmounts
    return () => {
      isCheckingRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [user?.id]); // Only depend on user.id to prevent unnecessary re-renders

  // Mark assessment as completed (hide it)
  const markAssessmentCompleted = useCallback(() => {
    setShouldShowAssessment(false);
    
    const userId = getConsistentUserId(user, null);
    if (userId) {
      // Update local storage to prevent showing again soon
      const completedTime = new Date().toISOString();
      localStorage.setItem(`assessment_completed_${userId}`, completedTime);
    }
  }, [user]);

  // Force refresh assessment status
  const refreshAssessmentStatus = useCallback(() => {
    const userId = getConsistentUserId(user, null);
    if (userId) {
      // Clear cache and recheck
      localStorage.removeItem(`assessment_check_${userId}`);
      
      // Don't use the callback here to avoid dependency issues
      // Instead inline the logic
      if (!isCheckingRef.current) {
        checkAssessmentEligibility();
      }
    }
  }, [user]); // Remove checkAssessmentEligibility from dependencies

  // Get days until next assessment
  const getDaysUntilNextAssessment = useCallback((): number | null => {
    if (!profile?.next_assessment_due) return null;
    
    const nextDue = new Date(profile.next_assessment_due);
    const now = new Date();
    const diffTime = nextDue.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }, [profile?.next_assessment_due]);

  return {
    shouldShowAssessment,
    profile,
    isLoading,
    lastCheckTime,
    markAssessmentCompleted,
    refreshAssessmentStatus,
    getDaysUntilNextAssessment,
    // Helper functions with safe defaults
    hasCompletedAssessment: !!(profile?.current_score),
    currentLevel: profile?.comprehension_level || 1,
    currentScore: profile?.current_score || 0,
    isEligibleForReassessment: !!(profile?.is_due_for_reassessment)
  };
}
